import { defineStore } from "pinia";

import { quarantineService } from "@/services";
import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";

import { Quarantine } from "@/models/Quarantine";
import { ModuleStatus } from "@/models/ModuleStatus";

interface State {
  entries: readonly Quarantine[];
  moduleStatus: ModuleStatus;
}
export const useQuarantineStore = defineStore("quarantine", {
  state: (): State => ({
    entries: [],
    moduleStatus: ModuleStatus.Initial,
  }),
  getters: {
    loading(): boolean {
      return this.moduleStatus !== ModuleStatus.Ready;
    },
    hasFiles(): boolean {
      return this.entries && this.entries.length > 0;
    },
    waitingCount(): number {
      return this.entries.filter((f) => f.isInStatus("Waiting")).length;
    },
  },
  actions: {
    async load(): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.quarantine === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      this.setStatus(ModuleStatus.Loading);

      try {
        const items = await quarantineService.getAll(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
        );
        this.loaded(items);
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async refresh(): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.quarantine === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      if (this.moduleStatus !== ModuleStatus.Ready) return;

      this.setStatus(ModuleStatus.Refreshing);

      try {
        const items = await quarantineService.getAll(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
        );
        this.loaded(items);
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },

    // Mutations
    loaded(items: Quarantine[]): void {
      this.entries = Object.freeze(items.map((i) => Object.assign(new Quarantine(), i)));
    },
    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.entries = [];
    },
  },
});
